import { useMemo, useState, useEffect, useCallback } from "react";
import { ChevronRight, Search, X, Edit2, Trash2, AlertTriangle, Plus, Building2, Filter, Calendar, Clock, List, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { stages, compactMoney, badgeTone, testid, errorMessage, getTodayDateString } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

export function NewOrderModal({ clients, initialClientId, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_id: initialClientId || clients[0]?.client_id || "",
    description: "",
    product: "",
    product_code: "",
    quantity: 100,
    unit: "pcs",
    unit_price: "",
    required_delivery_date: "",
    po_number: "",
    po_date: "",
    priority: "Normal",
    notes: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (initialClientId) {
      setForm((f) => ({ ...f, client_id: initialClientId }));
    }
  }, [initialClientId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const unitPriceVal = form.unit_price !== "" && form.unit_price !== null && !isNaN(Number(form.unit_price))
        ? Number(form.unit_price)
        : 0;
      const r = await api("post", "/orders", {
        ...form,
        quantity: Number(form.quantity),
        unit: form.unit || "pcs",
        unit_price: unitPriceVal,
      });
      toast.success(`Order ${r.data.order_id} created`);
      onCreated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Please complete the order details"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="new-order-form">
        <div className="modal-head">
          <div><div className="small-label">CENTRAL RECORD</div><h2>Create new order</h2></div>
          <button type="button" className="icon-btn" data-testid="new-order-close-button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label>
            Client
            <select
              data-testid="new-order-client-select"
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              required
            >
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.company_name || c.name || "Client"}
                </option>
              ))}
            </select>
          </label>
          <label>Description<input data-testid="new-order-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g., Matte finish 350 GSM cards" /></label>
          <label>Product / service<input data-testid="new-order-product-input" value={form.product} onChange={(e) => set("product", e.target.value)} required placeholder="e.g., Visiting Cards" /></label>
          <label>Product code<input data-testid="new-order-product-code-input" value={form.product_code} onChange={(e) => set("product_code", e.target.value)} /></label>
          <label>PO number<input data-testid="new-order-po-input" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} /></label>
          <label>Quantity<input data-testid="new-order-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required /></label>
          <label>Unit<input data-testid="new-order-unit-input" value={form.unit || "pcs"} onChange={(e) => set("unit", e.target.value)} placeholder="pcs, boxes" /></label>
          <label>Unit price (₹)<input data-testid="new-order-price-input" type="number" min="0" step="0.01" placeholder="Optional" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} /></label>
          <label>Required delivery<input data-testid="new-order-delivery-date-input" type="date" value={form.required_delivery_date} min={getTodayDateString()} onChange={(e) => set("required_delivery_date", e.target.value)} required /></label>
          <label>Priority<select data-testid="new-order-priority-select" value={form.priority} onChange={(e) => set("priority", e.target.value)}><option>Normal</option><option>High</option><option>Urgent</option></select></label>
        </div>
        <label className="full">Notes<textarea data-testid="new-order-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></label>
        {error && <div className="error" data-testid="new-order-error">{error}</div>}
        <button className="primary-btn wide" data-testid="new-order-submit-button" disabled={busy}>{busy ? "Creating…" : "Create order"} <ChevronRight size={16} /></button>
      </form>
    </div>
  );
}

export function EditOrderModal({ order, clients: initialClients, onClose, onUpdated }) {
  const [clients, setClients] = useState(initialClients || []);
  const [form, setForm] = useState({
    client_id: order.client_id || "",
    product: order.product || "",
    product_code: order.product_code || "",
    quantity: order.quantity || 1,
    unit_price: order.unit_price !== undefined && order.unit_price !== null ? order.unit_price : "",
    required_delivery_date: order.required_delivery_date || "",
    priority: order.priority || "Normal",
    po_number: order.po_number || "",
    po_date: order.po_date || "",
    notes: order.notes || "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!initialClients || initialClients.length === 0) {
      api("get", "/clients")
        .then((res) => {
          const list = (res.data || []).filter((c) => !c.deleted && c.status !== "Inactive" && c.status !== "Archived");
          setClients(list);
        })
        .catch(() => {});
    } else {
      const list = (initialClients || []).filter((c) => !c.deleted && c.status !== "Inactive" && c.status !== "Archived");
      setClients(list);
    }
  }, [initialClients]);

  const clientOptions = useMemo(() => {
    const list = [...clients];
    const currentId = order.client_id;
    if (currentId && !list.some((c) => c.client_id === currentId)) {
      list.unshift({
        client_id: currentId,
        company_name: order.client_name || currentId,
        contact_person: order.client_contact_person || "",
      });
    }
    return list;
  }, [clients, order]);

  useEffect(() => {
    if (!form.client_id) {
      const cid = order.client_id || (clientOptions[0]?.client_id || "");
      if (cid) setForm((f) => ({ ...f, client_id: cid }));
    }
  }, [order, clientOptions, form.client_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      setError("Please select a valid Client");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const unitPriceVal = form.unit_price !== "" && form.unit_price !== null && !isNaN(Number(form.unit_price))
        ? Number(form.unit_price)
        : 0;
      await api("patch", `/orders/${order.order_id}`, {
        ...form,
        quantity: Number(form.quantity),
        unit_price: unitPriceVal,
      });
      toast.success(`Order ${order.order_id} updated`);
      onUpdated();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not update order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="edit-order-form">
        <div className="modal-head">
          <div>
            <div className="small-label">EDIT ORDER</div>
            <h2>Order #{order.order_id}</h2>
            <p className="muted">{order.client_name}</p>
          </div>
          <button type="button" className="icon-btn" data-testid="edit-order-close-button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="form-grid">
          <label style={{ gridColumn: "1 / -1" }}>
            Client
            <select
              data-testid="edit-order-client-select"
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              required
            >
              <option value="" disabled>Select a client…</option>
              {clientOptions.map((c) => (
                <option key={c.client_id} value={c.client_id}>
                  {c.company_name || c.name || "Client"}{c.contact_person ? ` (${c.contact_person})` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>Product / service<input data-testid="edit-order-product-input" value={form.product} onChange={(e) => set("product", e.target.value)} required /></label>
          <label>Product code<input data-testid="edit-order-product-code-input" value={form.product_code} onChange={(e) => set("product_code", e.target.value)} /></label>
          <label>PO number<input data-testid="edit-order-po-input" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} /></label>
          <label>PO date<input data-testid="edit-order-po-date-input" type="date" value={form.po_date} min={getTodayDateString()} onChange={(e) => set("po_date", e.target.value)} /></label>
          <label>Quantity<input data-testid="edit-order-quantity-input" type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required /></label>
          <label>Unit price (₹)<input data-testid="edit-order-price-input" type="number" min="0" step="0.01" placeholder="Optional" value={form.unit_price} onChange={(e) => set("unit_price", e.target.value)} /></label>
          <label>Required delivery<input data-testid="edit-order-delivery-date-input" type="date" value={form.required_delivery_date} min={getTodayDateString()} onChange={(e) => set("required_delivery_date", e.target.value)} required /></label>
          <label>Priority<select data-testid="edit-order-priority-select" value={form.priority} onChange={(e) => set("priority", e.target.value)}><option>Normal</option><option>High</option><option>Urgent</option></select></label>
        </div>
        <label className="full">Notes<textarea data-testid="edit-order-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></label>
        {error && <div className="error" data-testid="edit-order-error">{error}</div>}
        <button className="primary-btn wide" data-testid="edit-order-submit-button" disabled={busy}>
          {busy ? "Saving…" : "Save changes"} <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
}

export function DeleteOrderConfirmationModal({ order, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await api("delete", `/orders/${order.order_id}`);
      toast.success(`Order ${order.order_id} deleted`);
      onDeleted();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err, "Could not delete order"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
        data-testid="delete-order-confirmation-modal"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: "0 0 6px 0", fontSize: 18, color: "var(--navy)" }}>
              Delete Order #{order.order_id}
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: "#64748b", lineHeight: 1.5 }}>
              Are you sure you want to delete this order?
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div
          style={{
            background: "#f8fafc",
            border: "1px solid var(--line)",
            borderRadius: 6,
            padding: "12px 14px",
            marginBottom: 20,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <div style={{ color: "var(--navy)", fontWeight: 600 }}>{order.product}</div>
          <div style={{ color: "#64748b" }}>{order.client_name} · {(order.quantity || 0).toLocaleString()} units</div>
          <p style={{ margin: "8px 0 0", color: "#94a3b8", fontSize: 12 }}>
            This order will be removed from your active register. All historical client and file links will be maintained.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-delete-order-button"
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleDelete}
            disabled={busy}
            style={{ background: "var(--red)", borderColor: "var(--red)" }}
            data-testid="confirm-delete-order-button"
          >
            {busy ? "Deleting…" : "Delete Order"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Orders({ orders = [], clients = [], onOpenClientOrders, onOpen, onCreated, user, routeParams, onNavigate }) {
  const [search, setSearch] = useSavedState("orders", "search", "");
  const [stage, setStage] = useSavedState("orders", "stage", routeParams?.stage || "");
  const [filter, setFilter] = useSavedState("orders", "filter", routeParams?.filter || "");
  const [month, setMonth] = useSavedState("orders", "month", routeParams?.month || "");
  const [company, setCompany] = useSavedState("orders", "company", routeParams?.company || "");
  const [viewMode, setViewMode] = useState(() => (routeParams?.filter || routeParams?.month ? "orders" : "clients"));
  const [showNew, setShowNew] = useState(false);
  const canManage = isAdmin(user) || canAccessDepartment(user, ["Sales"]);

  // Sync state if routeParams change
  useEffect(() => {
    if (routeParams) {
      if (routeParams.stage !== undefined) setStage(routeParams.stage || "");
      if (routeParams.filter !== undefined) {
        setFilter(routeParams.filter || "");
        if (routeParams.filter) setViewMode("orders");
      }
      if (routeParams.month !== undefined) {
        setMonth(routeParams.month || "");
        if (routeParams.month) setViewMode("orders");
      }
      if (routeParams.company !== undefined) setCompany(routeParams.company || "");
    }
  }, [routeParams, setStage, setFilter, setMonth, setCompany]);

  // Workflow summary metrics across all active orders
  const metrics = useMemo(() => {
    const activeOrders = orders.filter((o) => !o.deleted);
    return {
      orderReceived: activeOrders.filter((o) => o.current_stage === "Order Received").length,
      design: activeOrders.filter((o) => ["Design", "Client Approval"].includes(o.current_stage)).length,
      production: activeOrders.filter((o) => o.current_stage === "Production").length,
      dispatch: activeOrders.filter((o) => ["Dispatch", "Challan", "Delivery"].includes(o.current_stage)).length,
    };
  }, [orders]);

  // Helper: Match order against active filters
  const isOrderMatchingFilters = useCallback(
    (o, q = "") => {
      // 1. Company filter
      if (company && company !== "All Companies") {
        const oComp = (o.company || "").toLowerCase();
        if (oComp && oComp !== company.toLowerCase()) return false;
      }

      // 2. Stage filter
      if (stage) {
        if (stage === "Dispatch") {
          if (!["Dispatch", "Challan", "Delivery"].includes(o.current_stage)) return false;
        } else if (stage === "Design") {
          if (!["Design", "Client Approval"].includes(o.current_stage)) return false;
        } else if (o.current_stage !== stage) {
          return false;
        }
      }

      // 3. Special filter (e.g. overdue)
      if (filter === "overdue") {
        const isDel = o.delivery_status === "Delivered" || (o.current_stage || "").toLowerCase() === "delivered";
        const reqDate = (o.required_delivery_date || "").slice(0, 10);
        const today = getTodayDateString();
        const isPast = reqDate && reqDate < today;
        if (isDel || !isPast) return false;
      }

      // 4. Month filter (e.g. "2026-10")
      if (month) {
        const oMonth = (o.order_date || o.created_at || "").slice(0, 7);
        if (oMonth !== month) return false;
      }

      // 5. Search query matching order attributes
      if (q) {
        const matchOrder =
          (o.order_id || "").toLowerCase().includes(q) ||
          (o.description || "").toLowerCase().includes(q) ||
          (o.product || "").toLowerCase().includes(q) ||
          (o.product_code || "").toLowerCase().includes(q) ||
          (o.po_number || "").toLowerCase().includes(q) ||
          (o.client_name || "").toLowerCase().includes(q);
        if (!matchOrder) return false;
      }

      return true;
    },
    [company, stage, filter, month]
  );

  // Group orders by unique client
  const clientGroups = useMemo(() => {
    const map = new Map();

    (orders || []).forEach((o) => {
      if (o.deleted) return;

      const key = o.client_id || o.client_name?.trim() || "unknown";
      if (!map.has(key)) {
        const clientObj = (clients || []).find(
          (c) => c.client_id === o.client_id || c.company_name?.toLowerCase() === o.client_name?.toLowerCase()
        );

        map.set(key, {
          clientId: o.client_id || clientObj?.client_id || key,
          clientName: o.client_name || clientObj?.company_name || key,
          contactPerson: o.client_contact_person || clientObj?.contact_person || "—",
          email: o.client_email || clientObj?.email || "—",
          phone: o.client_phone || clientObj?.phone || "",
          company: o.company || clientObj?.company || "",
          allOrders: [],
        });
      }

      map.get(key).allOrders.push(o);
    });

    const list = Array.from(map.values()).map((group) => {
      // Sort orders descending by date
      group.allOrders.sort((a, b) => {
        const dateA = new Date(a.order_date || a.created_at || 0).getTime();
        const dateB = new Date(b.order_date || b.created_at || 0).getTime();
        return dateB - dateA;
      });

      const totalOrders = group.allOrders.length;
      const completedOrders = group.allOrders.filter((o) =>
        ["Delivered", "Completed", "Archived"].includes(o.current_stage)
      ).length;
      const activeOrders = totalOrders - completedOrders;
      const latestOrder = group.allOrders[0] || null;

      return {
        ...group,
        totalOrders,
        activeOrders,
        completedOrders,
        latestOrder,
        latestOrderId: latestOrder?.order_id || "—",
        latestOrderDate: latestOrder?.order_date || latestOrder?.created_at || "—",
        latestOrderStage: latestOrder?.current_stage || "—",
        latestOrderProduct: latestOrder?.product || "—",
      };
    });

    // Sort clients by most recent order
    list.sort((a, b) => {
      const dateA = new Date(a.latestOrderDate !== "—" ? a.latestOrderDate : 0).getTime();
      const dateB = new Date(b.latestOrderDate !== "—" ? b.latestOrderDate : 0).getTime();
      return dateB - dateA;
    });

    return list;
  }, [orders, clients]);

  // Filter clients and their orders by search & active filters
  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();

    return clientGroups
      .map((client) => {
        const matchingOrders = client.allOrders.filter((o) => {
          if (!isOrderMatchingFilters(o)) return false;

          // Search query matching order or client attributes
          if (q) {
            const matchOrder =
              (o.order_id || "").toLowerCase().includes(q) ||
              (o.description || "").toLowerCase().includes(q) ||
              (o.product || "").toLowerCase().includes(q) ||
              (o.product_code || "").toLowerCase().includes(q) ||
              (o.po_number || "").toLowerCase().includes(q);

            const matchClient =
              (client.clientName || "").toLowerCase().includes(q) ||
              (client.contactPerson || "").toLowerCase().includes(q);

            if (!matchOrder && !matchClient) return false;
          }

          return true;
        });

        if (matchingOrders.length === 0) return null;

        const matchingActive = matchingOrders.filter(
          (o) => !["Delivered", "Completed", "Archived"].includes(o.current_stage)
        ).length;
        const matchingCompleted = matchingOrders.length - matchingActive;
        const latestMatching = matchingOrders[0] || client.latestOrder;

        return {
          ...client,
          matchingOrders,
          matchingOrdersCount: matchingOrders.length,
          activeOrdersCount: stage || filter || month || q ? matchingActive : client.activeOrders,
          completedOrdersCount: stage || filter || month || q ? matchingCompleted : client.completedOrders,
          displayLatestId: latestMatching?.order_id || client.latestOrderId,
          displayLatestDate: latestMatching?.order_date || client.latestOrderDate,
          displayLatestStage: latestMatching?.current_stage || client.latestOrderStage,
        };
      })
      .filter(Boolean);
  }, [clientGroups, search, isOrderMatchingFilters, stage, filter, month]);

  // Flat matching orders list for direct orders view
  const matchingFlatOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (orders || []).filter((o) => !o.deleted && isOrderMatchingFilters(o, q));
    list.sort((a, b) => {
      const dateA = new Date(a.order_date || a.created_at || 0).getTime();
      const dateB = new Date(b.order_date || b.created_at || 0).getTime();
      return dateB - dateA;
    });
    return list;
  }, [orders, search, isOrderMatchingFilters]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    paginatedItems: paginatedClients,
    totalItems: totalFilteredClients,
  } = usePagination(filteredClients, 10, { sectionKey: "orders" });

  const {
    currentPage: ordersPage,
    setCurrentPage: setOrdersPage,
    pageSize: ordersPageSize,
    setPageSize: setOrdersPageSize,
    paginatedItems: paginatedFlatOrders,
    totalItems: totalFlatOrders,
  } = usePagination(matchingFlatOrders, 10, { sectionKey: "orders_flat" });

  const handleSelectClient = (client) => {
    if (onOpenClientOrders) {
      onOpenClientOrders(client.clientId || client.clientName);
    } else if (onOpen && client.latestOrderId && client.latestOrderId !== "—") {
      onOpen(client.latestOrderId);
    }
  };

  const hasActiveFilters = Boolean(stage || filter || month || (company && company !== "All Companies") || search);

  const handleClearFilters = () => {
    setStage("");
    setFilter("");
    setMonth("");
    setSearch("");
    if (company && company !== "All Companies") {
      setCompany("");
    }
    setViewMode("clients");
    setCurrentPage(1);
    setOrdersPage(1);
    if (window.history.pushState) {
      window.history.pushState({}, "", "/orders");
    }
    toast.success("Filters cleared");
  };

  return (
    <div className="content" data-testid="orders-page">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">OPERATIONS / CENTRAL REGISTER</div>
          <h1>Orders</h1>
          <p className="muted">Client-wise register of every ongoing and completed commitment.</p>
        </div>
        {canManage && (
          <button className="primary-btn compact" data-testid="orders-new-button" onClick={() => setShowNew(true)}>
            <Plus size={15} /> New order
          </button>
        )}
      </div>

      {/* Summary / Status Cards (Interactive Stage Filters) */}
      <div className="metrics-grid">
        <MetricBox
          label="Order Received"
          value={metrics.orderReceived}
          tone="blue"
          active={stage === "Order Received"}
          onClick={() => {
            setStage(stage === "Order Received" ? "" : "Order Received");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Design"
          value={metrics.design}
          tone="purple"
          active={stage === "Design" || stage === "Client Approval"}
          onClick={() => {
            setStage(stage === "Design" ? "" : "Design");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Production"
          value={metrics.production}
          tone="amber"
          active={stage === "Production"}
          onClick={() => {
            setStage(stage === "Production" ? "" : "Production");
            setCurrentPage(1);
          }}
        />
        <MetricBox
          label="Dispatch"
          value={metrics.dispatch}
          tone="green"
          active={stage === "Dispatch" || stage === "Challan" || stage === "Delivery"}
          onClick={() => {
            setStage(stage === "Dispatch" ? "" : "Dispatch");
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Active Filter Banner */}
      {hasActiveFilters && (
        <div className="active-filter-banner" data-testid="orders-active-filter-banner">
          <div className="filter-title">
            <Filter size={15} />
            <span>Active Filter:</span>
            {filter === "overdue" && (
              <span className="badge red" style={{ fontWeight: 700 }} data-testid="active-filter-overdue">
                Overdue Orders ({matchingFlatOrders.length})
              </span>
            )}
            {month && (
              <span className="badge blue" style={{ fontWeight: 700 }} data-testid="active-filter-month">
                Period: {month} ({matchingFlatOrders.length} orders)
              </span>
            )}
            {stage && (
              <span className="badge amber" style={{ fontWeight: 700 }} data-testid="active-filter-stage">
                Stage: {stage}
              </span>
            )}
            {company && company !== "All Companies" && (
              <span className="badge purple" style={{ fontWeight: 700 }} data-testid="active-filter-company">
                Company: {company}
              </span>
            )}
            {search && (
              <span className="badge slate" style={{ fontWeight: 700 }}>
                Search: "{search}"
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", background: "#e2e8f0", padding: 2, borderRadius: 6 }}>
              <button
                type="button"
                onClick={() => setViewMode("orders")}
                style={{
                  border: 0,
                  padding: "4px 10px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: viewMode === "orders" ? 700 : 500,
                  background: viewMode === "orders" ? "#fff" : "transparent",
                  color: viewMode === "orders" ? "var(--navy)" : "#64748b",
                  cursor: "pointer",
                }}
                data-testid="orders-view-mode-orders"
              >
                Orders List ({matchingFlatOrders.length})
              </button>
              <button
                type="button"
                onClick={() => setViewMode("clients")}
                style={{
                  border: 0,
                  padding: "4px 10px",
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: viewMode === "clients" ? 700 : 500,
                  background: viewMode === "clients" ? "#fff" : "transparent",
                  color: viewMode === "clients" ? "var(--navy)" : "#64748b",
                  cursor: "pointer",
                }}
                data-testid="orders-view-mode-clients"
              >
                By Client ({filteredClients.length})
              </button>
            </div>

            <button
              type="button"
              className="clear-btn"
              onClick={handleClearFilters}
              data-testid="orders-clear-filters-button"
            >
              <X size={13} /> Clear filter
            </button>
          </div>
        </div>
      )}

      {/* Search & Filters Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            data-testid="orders-search-input"
            placeholder="Search client name, contact, order ID, product…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
              setOrdersPage(1);
            }}
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                setCurrentPage(1);
                setOrdersPage(1);
              }}
              data-testid="orders-search-clear"
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <select
          data-testid="orders-stage-filter"
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setCurrentPage(1);
            setOrdersPage(1);
          }}
        >
          <option value="">All stages</option>
          {stages.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Main View: Flat Orders Table OR Client-wise Groups Table */}
      {viewMode === "orders" ? (
        <section className="panel" data-testid="orders-flat-table-panel">
          <div className="panel-title">
            <div>
              <h3>
                Orders <span className="count">{matchingFlatOrders.length}</span>
              </h3>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>
              Click any order to view details
            </span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Client</th>
                  <th>Product</th>
                  <th>Required Delivery</th>
                  <th>Value</th>
                  <th>Stage</th>
                  <th style={{ textAlign: "right", width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFlatOrders.map((o) => {
                  const today = getTodayDateString();
                  const isOverdue =
                    o.delivery_status !== "Delivered" &&
                    (o.current_stage || "").toLowerCase() !== "delivered" &&
                    (o.required_delivery_date || "").slice(0, 10) !== "" &&
                    (o.required_delivery_date || "").slice(0, 10) < today;

                  return (
                    <tr
                      key={o.order_id}
                      onClick={() => onOpen && onOpen(o.order_id)}
                      data-testid={`order-row-${o.order_id}`}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <b className="mono" style={{ color: "#0284c7", fontSize: 13 }}>
                          {o.order_id}
                        </b>
                        <small style={{ display: "block", color: "#64748b" }}>
                          {o.order_date || o.created_at || "—"}
                        </small>
                      </td>
                      <td>
                        <b style={{ fontSize: 13.5, color: "var(--navy)" }}>{o.client_name || "—"}</b>
                        <small style={{ display: "block", color: "#64748b" }}>
                          {o.po_number ? `PO: ${o.po_number}` : ""}
                        </small>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{o.product}</div>
                        <small style={{ color: "#64748b" }}>
                          {(o.quantity || 0).toLocaleString()} {o.unit || "pcs"}
                        </small>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: isOverdue ? 700 : 500, color: isOverdue ? "#dc2626" : "inherit" }}>
                            {o.required_delivery_date || "—"}
                          </span>
                          {isOverdue && (
                            <span className="badge red" style={{ fontSize: 10, padding: "1px 5px" }}>
                              Overdue
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <b>{compactMoney(o.total_value)}</b>
                      </td>
                      <td>
                        <span className={`badge ${badgeTone(o.current_stage)}`}>
                          {o.current_stage}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="outline-btn compact"
                          style={{ fontSize: 12, padding: "4px 10px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen && onOpen(o.order_id);
                          }}
                          data-testid={`btn-view-order-${o.order_id}`}
                        >
                          View Order <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {paginatedFlatOrders.length === 0 && (
              <div className="empty-inline" data-testid="orders-empty-state">
                {hasActiveFilters ? "No orders match your filter criteria." : "No orders found."}
              </div>
            )}
          </div>

          <Pagination
            currentPage={ordersPage}
            totalItems={totalFlatOrders}
            pageSize={ordersPageSize}
            onPageChange={setOrdersPage}
            onPageSizeChange={setOrdersPageSize}
            itemLabel="orders"
          />
        </section>
      ) : (
        <section className="panel" data-testid="client-orders-grouped-panel">
          <div className="panel-title">
            <div>
              <h3>
                Clients <span className="count">{filteredClients.length}</span>
              </h3>
            </div>
            <span className="muted" style={{ fontSize: 12 }}>
              Click any client to view their orders
            </span>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Contact Person</th>
                  <th>Orders</th>
                  <th>Active / Completed</th>
                  <th>Latest Order</th>
                  <th>Latest Stage</th>
                  <th style={{ textAlign: "right", width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClients.map((c) => (
                  <tr
                    key={c.clientId || c.clientName}
                    onClick={() => handleSelectClient(c)}
                    data-testid={`client-group-row-${c.clientId}`}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <b style={{ fontSize: 13.5, color: "var(--navy)" }}>{c.clientName}</b>
                      <small className="mono" style={{ display: "block", color: "#64748b" }}>
                        {c.clientId}
                      </small>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{c.contactPerson}</div>
                      {canViewClientPhone(user) && c.phone && (
                        <small style={{ color: "#64748b", display: "block" }}>📞 {c.phone}</small>
                      )}
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, fontSize: 13.5, color: "var(--navy)" }}>
                        {c.matchingOrdersCount}
                      </span>{" "}
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {c.matchingOrdersCount === 1 ? "order" : "orders"}
                      </span>
                      {hasActiveFilters && c.matchingOrdersCount !== c.totalOrders && (
                        <small style={{ display: "block", color: "#94a3b8", fontSize: 11 }}>
                          of {c.totalOrders} total
                        </small>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span className="badge blue" style={{ fontSize: 11, padding: "2px 7px" }}>
                          {c.activeOrdersCount} Active
                        </span>
                        {c.completedOrdersCount > 0 && (
                          <span className="badge slate" style={{ fontSize: 11, padding: "2px 7px" }}>
                            {c.completedOrdersCount} Completed
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <b className="mono" style={{ color: "#0284c7", fontSize: 12.5 }}>
                        {c.displayLatestId}
                      </b>
                      <small style={{ display: "block", color: "#64748b" }}>
                        {c.displayLatestDate || "—"}
                      </small>
                    </td>
                    <td>
                      <span className={`badge ${badgeTone(c.displayLatestStage)}`}>
                        {c.displayLatestStage || "—"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        className="outline-btn compact"
                        style={{ fontSize: 12, padding: "4px 10px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectClient(c);
                        }}
                        data-testid={`btn-view-client-${c.clientId}`}
                      >
                        View Orders <ChevronRight size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredClients.length === 0 && (
              <div className="empty-inline" data-testid="orders-empty-state">
                {hasActiveFilters
                  ? "No clients match your filter criteria."
                  : "No client orders found."}
              </div>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredClients}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            itemLabel="clients"
          />
        </section>
      )}

      {/* Create Order Modal */}
      {showNew && (
        <NewOrderModal
          clients={clients}
          onClose={() => setShowNew(false)}
          onCreated={onCreated}
        />
      )}
    </div>
  );
}

function MetricBox({ label, value, tone = "blue", onClick, active }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {})
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={`stage-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
      </div>
      <strong>{value}</strong>
    </div>
  );
}
